/**
 * AnnotationChip — the non-editable summary of a captured brightmark annotation, shown at
 * the top of the FeedbackModal when annotate mode produced a target.
 *
 * Summarises the target: a "Text" / "Element" badge, the source hint (e.g. `data-testid=lead-row`)
 * or the element tag, and a truncated text snippet. A thumbnail of the element screenshot appears
 * once the (async, best-effort) snapdom raster resolves. A ✕ discards the annotation — the modal
 * falls back to a plain report (no annotation, no auto-attached screenshot).
 */
import { MousePointerClick, TextCursorInput, X } from 'lucide-react'
import type { BrightmarkAnnotation } from '@/lib/brightmark/types'

interface AnnotationChipProps {
  annotation: BrightmarkAnnotation
  /** Object URL for the element-screenshot thumbnail, or null while it resolves / on failure. */
  thumbnailUrl: string | null
  onDiscard: () => void
}

/** A short, human label for what the annotation points at. */
function targetLabel(a: BrightmarkAnnotation): string {
  if (a.source_hint_attr) return a.source_hint_attr
  // Fall back to the leading tag of the captured HTML, e.g. "<button …>" → "button".
  const tagMatch = a.element_html?.match(/^<([a-zA-Z][\w-]*)/)
  if (tagMatch?.[1]) return tagMatch[1]
  return a.anchor_kind === 'text' ? 'selected text' : 'element'
}

/** A truncated text snippet for the chip body (quote for text anchors, snippet otherwise). */
function snippetText(a: BrightmarkAnnotation): string | null {
  const raw = a.anchor_kind === 'text' ? a.quote_exact : a.text_snippet
  if (!raw) return null
  const flat = raw.replace(/\s+/g, ' ').trim()
  return flat.length > 80 ? `${flat.slice(0, 80)}…` : flat
}

export function AnnotationChip({ annotation, thumbnailUrl, onDiscard }: AnnotationChipProps) {
  const isText = annotation.anchor_kind === 'text'
  const Icon = isText ? TextCursorInput : MousePointerClick
  const snippet = snippetText(annotation)

  return (
    <div
      data-testid="annotation-chip"
      className="border-l-primary bg-muted/40 flex items-start gap-3 rounded-lg border border-l-[3px] p-3"
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt="Captured element"
          className="size-12 shrink-0 rounded border object-cover"
        />
      ) : (
        <span className="bg-background flex size-12 shrink-0 items-center justify-center rounded border">
          <Icon className="text-primary size-5" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 text-sm font-medium">
          <Icon className="text-primary size-3.5" />
          {isText ? 'Text' : 'Element'} · {targetLabel(annotation)}
        </p>
        <p className="text-muted-foreground mt-0.5 truncate text-xs">
          {snippet ?? annotation.route}
        </p>
      </div>
      <button
        type="button"
        onClick={onDiscard}
        aria-label="Discard annotation"
        title="Remove this annotation"
        className="text-muted-foreground hover:text-foreground -m-1 rounded-md p-2"
      >
        <X className="size-4" />
      </button>
    </div>
  )
}
